import type { ComponentChildren } from 'preact';
import { computed, signal } from '@preact/signals';

export interface PageFilterGroup {
  id: string;
  title: string;
  /** Controls of the group, rendered inside the single mobile sheet. */
  node: ComponentChildren;
  /** Filters away from their default value, added up on the button. */
  activeCount: number;
}

/**
 * On a phone every page has ONE entry for its filters: the groups register here and the
 * page header renders a single "Filtros" button with all of them in one sheet. Before,
 * each group painted its own card and the data started below three or four of them.
 */
const groups = signal<PageFilterGroup[]>([]);

export const pageFilterGroups = computed(() => groups.value);

export const pageFilterCount = computed(() =>
  groups.value.reduce((total, group) => total + group.activeCount, 0),
);

export function registerPageFilters(group: PageFilterGroup): () => void {
  groups.value = [...groups.value.filter((g) => g.id !== group.id), group];
  nodes.set(group.id, group.node);
  return () => {
    nodes.delete(group.id);
    groups.value = groups.value.filter((g) => g.id !== group.id);
  };
}

/**
 * Nodos de cada grupo, fuera del signal de grupos. `PageFilters` los actualiza
 * tras CADA render; si eso notificaba el signal, la cabecera (que lo lee) se
 * redibujaba y con ella volvia a renderizarse el grupo: bucle sin fin que
 * congelaba la pestaña en movil (Landings y funnel a 390 px, 2026-09-30).
 */
const nodes = new Map<string, ComponentChildren>();

/** Sube cuando cambia algún nodo; solo lo lee el panel de filtros abierto. */
export const pageFilterNodesVersion = signal(0);

export function pageFilterNode(id: string, fallback: ComponentChildren): ComponentChildren {
  return nodes.has(id) ? nodes.get(id) : fallback;
}

/** Keeps the node and the counter of an already registered group up to date. */
export function updatePageFilters(group: PageFilterGroup): void {
  const current = groups.value.find((g) => g.id === group.id);
  if (!current) return;
  nodes.set(group.id, group.node);
  // Solo notifica a la cabecera si cambia lo que ella pinta (título o contador).
  if (current.title !== group.title || current.activeCount !== group.activeCount) {
    groups.value = groups.value.map((g) => (g.id === group.id ? group : g));
  }
  // El panel abierto se entera en el siguiente microtask, fuera del render.
  queueMicrotask(() => {
    pageFilterNodesVersion.value += 1;
  });
}
